import { useEffect, useState } from 'react'

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import { Alert, Avatar, Button } from '@mui/material'
import Box from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { TreeItem, treeItemClasses } from '@mui/x-tree-view/TreeItem'
import PropTypes from 'prop-types'

const StyledTreeItemRoot = styled(TreeItem)(({ theme }) => ({
  color: theme.palette.text.secondary,
  [`& .${treeItemClasses.content}`]: {
    color: theme.palette.text.secondary,
    paddingRight: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
    '&.Mui-expanded': {
      fontWeight: theme.typography.fontWeightRegular,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused': {
      backgroundColor: `var(--tree-view-bg-color, ${theme.palette.action.selected})`,
      color: 'var(--tree-view-color)',
    },
    [`& .${treeItemClasses.label}`]: {
      fontWeight: 'inherit',
      color: 'inherit',
    },
  },
  [`& .${treeItemClasses.group}`]: {
    marginLeft: '40px',
    [`& .${treeItemClasses.content}`]: {
      paddingLeft: theme.spacing(2),
    },
  },
}))

function StyledTreeItem(props) {
  const { bgColor, color, labelInfo, labelText, ...other } = props
  return (
    <StyledTreeItemRoot
      label={
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left', p: 0.5, pr: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="h6">{labelText}</Typography>
          </div>
          {labelInfo.map((employee, index) => (
            <div
              key={`${index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                marginBottom: '8px',
              }}
            >
              <Avatar
                src={`/assets/images/${employee.imagen}`}
                sx={{
                  transition: '.3s',
                  backgroundColor: 'secondary.main',
                  aspectRatio: '4/4',
                  width: '30px',
                  height: '30px',
                  mr: 2,
                  color: 'secondary.contrastText',
                }}
              />
              <Typography variant="body1">
                {[employee.nombre, employee.primer_apellido, employee.segundo_apellido]
                  .filter(Boolean)
                  .join(' ')}
              </Typography>
            </div>
          ))}
        </Box>
      }
      style={{
        '--tree-view-color': color,
        '--tree-view-bg-color': bgColor,
      }}
      {...other}
    />
  )
}

StyledTreeItem.propTypes = {
  bgColor: PropTypes.string,
  color: PropTypes.string,
  labelInfo: PropTypes.array,
  labelText: PropTypes.string.isRequired,
}

const getStructureIds = (nodes) => {
  let ids = []
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i]
    ids.push(node.id.toString())
    if (node.hijos.length > 0) {
      const children = getStructureIds(node.hijos)
      ids = ids.concat(children)
    }
  }
  return ids
}

const Estructura = ({ response }) => {
  const [organigrama, setOrganigrama] = useState(response)
  const [structureItems, setStructureItems] = useState([])
  const [expandedItems, setExpandedItems] = useState([])

  useEffect(() => {
    setOrganigrama(response)
  }, [response])

  useEffect(() => {
    let itemIds = []
    if (organigrama) {
      for (let i = 0; i < organigrama.length; i++) {
        const node = organigrama[i]
        itemIds.push(node.id.toString())
        if (node.hijos.length > 0) {
          const children = getStructureIds(node.hijos)
          itemIds = itemIds.concat(children)
        }
      }
    }
    setExpandedItems(itemIds)
    setStructureItems(itemIds)
  }, [organigrama])

  const nodos = (datos) => {
    const listaNodos = datos.map((rama, idx) => {
      return rama.hijos ? (
        <StyledTreeItem
          itemId={rama.id.toString()}
          key={`id_${idx}${rama.id.toString()}`}
          labelText={rama.descripcion}
          labelInfo={rama.empleados}
        >
          {nodos(rama.hijos)}
        </StyledTreeItem>
      ) : (
        <StyledTreeItem
          itemId={rama.id.toString()}
          key={`id_${idx}${rama.id.toString()}`}
          labelText={rama.descripcion}
          labelInfo={rama.empleados}
        />
      )
    })
    return listaNodos
  }

  const hasData = organigrama && organigrama.length > 0

  const handleExpandedItemsChange = (event, itemIds) => {
    setExpandedItems(itemIds)
  }

  const handleExpandClick = () => {
    setExpandedItems((oldExpanded) => (oldExpanded.length === 0 ? structureItems : []))
  }

  if (!hasData) {
    return (
      <Alert severity="warning">
        No hemos podido obtener el organigrama para este usuario. Puede que se encuentre mal
        parametrizado
      </Alert>
    )
  }

  return (
    <Box>
      <Button onClick={handleExpandClick}>
        {expandedItems.length === 0 ? 'Expandir Todo' : 'Colapsar Todo'}
      </Button>
      <SimpleTreeView
        aria-label="organigrama"
        defaultCollapseIcon={<ArrowDropDownIcon />}
        defaultExpandIcon={<ArrowRightIcon />}
        defaultEndIcon={<div style={{ width: 24 }} />}
        expandedItems={expandedItems}
        onExpandedItemsChange={handleExpandedItemsChange}
      >
        {nodos(organigrama)}
      </SimpleTreeView>
    </Box>
  )
}

Estructura.propTypes = {
  response: PropTypes.array,
}

export default Estructura
